/**
 * Pure validators for platform kind `whiteboard`.
 * Mirror: src/widgets/kinds/whiteboard-state.ts (keep in lockstep).
 */

export const WHITEBOARD_FORMAT = 'utarus-whiteboard-v1' as const;

export const WHITEBOARD_STROKES_MAX = 500;
export const WHITEBOARD_TEXTS_MAX = 50;
export const WHITEBOARD_POINTS_TOTAL_MAX = 50_000;
export const WHITEBOARD_TEXT_MAX = 500;
export const WHITEBOARD_DIM_MIN = 200;
export const WHITEBOARD_DIM_MAX = 4000;
export const WHITEBOARD_STROKE_WIDTH_MAX = 64;
export const WHITEBOARD_FONT_SIZE_MAX = 200;
export const WHITEBOARD_COLOR_MAX = 32;
export const WHITEBOARD_SUBMIT_LABEL_MAX = 40;

/**
 * Ids: any 8-4-4-4-12 hex UUID shape (not RFC version/variant bits).
 * Same policy as rich-document comments.
 */
const UUID_SHAPE_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Control chars except space/tab — also rejects newlines (\n, \r). */
const CONTROL_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F]/
/** Stricter for text labels: no control chars except space/tab (reject \n/\r). */
const TEXT_LABEL_CONTROL = /[\x00-\x08\x0A-\x1F]/

export type WhiteboardStroke = {
  id: string;
  tool: 'pen';
  color: string;
  width: number;
  points: Array<[number, number]>;
};

export type WhiteboardText = {
  id: string;
  x: number;
  y: number;
  text: string;
  color: string;
  fontSize: number;
};

export interface WhiteboardStateV1 {
  format: typeof WHITEBOARD_FORMAT;
  width: number;
  height: number;
  background: string;
  strokes: WhiteboardStroke[];
  texts: WhiteboardText[];
}

export type WhiteboardProps = {
  mode?: 'edit' | 'view';
  bgColor?: string;
  allowSubmit?: boolean;
  submitLabel?: string;
};

export type ValidateResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function validateDim(
  value: unknown,
  field: 'width' | 'height',
): ValidateResult<number> {
  if (!isFiniteNumber(value) || !Number.isInteger(value)) {
    return {
      ok: false,
      error: `whiteboard state.${field} must be an integer`,
    };
  }
  if (value < WHITEBOARD_DIM_MIN || value > WHITEBOARD_DIM_MAX) {
    return {
      ok: false,
      error: `whiteboard state.${field} must be between ${WHITEBOARD_DIM_MIN} and ${WHITEBOARD_DIM_MAX}`,
    };
  }
  return { ok: true, value };
}

function validateColorString(
  value: unknown,
  path: string,
): ValidateResult<string> {
  if (typeof value !== 'string') {
    return { ok: false, error: `${path} must be a string` };
  }
  if (!value) {
    return { ok: false, error: `${path} must be non-empty` };
  }
  if (value.length > WHITEBOARD_COLOR_MAX) {
    return {
      ok: false,
      error: `${path} exceeds ${WHITEBOARD_COLOR_MAX} characters`,
    };
  }
  if (CONTROL_CHARS.test(value)) {
    return { ok: false, error: `${path} contains control characters` };
  }
  return { ok: true, value };
}

function validateStroke(
  raw: unknown,
  index: number,
): ValidateResult<WhiteboardStroke> {
  if (!isPlainObject(raw)) {
    return { ok: false, error: `whiteboard strokes[${index}] must be a plain object` };
  }
  const allowed = new Set(['id', 'tool', 'color', 'width', 'points']);
  for (const k of Object.keys(raw)) {
    if (!allowed.has(k)) {
      return {
        ok: false,
        error: `whiteboard strokes[${index}] unknown field: ${k}`,
      };
    }
  }
  if (typeof raw.id !== 'string' || !UUID_SHAPE_RE.test(raw.id)) {
    return {
      ok: false,
      error: `whiteboard strokes[${index}].id must be a UUID (8-4-4-4-12 hex, e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890)`,
    };
  }
  if (raw.tool !== 'pen') {
    return {
      ok: false,
      error: `whiteboard strokes[${index}].tool must be 'pen', got ${String(raw.tool)}`,
    };
  }
  const colorR = validateColorString(raw.color, `whiteboard strokes[${index}].color`);
  if (!colorR.ok) return colorR;

  if (!isFiniteNumber(raw.width) || raw.width <= 0) {
    return {
      ok: false,
      error: `whiteboard strokes[${index}].width must be a positive number`,
    };
  }
  if (raw.width > WHITEBOARD_STROKE_WIDTH_MAX) {
    return {
      ok: false,
      error: `whiteboard strokes[${index}].width exceeds ${WHITEBOARD_STROKE_WIDTH_MAX}`,
    };
  }

  if (!Array.isArray(raw.points)) {
    return {
      ok: false,
      error: `whiteboard strokes[${index}].points must be an array`,
    };
  }
  if (raw.points.length < 1) {
    return {
      ok: false,
      error: `whiteboard strokes[${index}].points must have length ≥ 1`,
    };
  }
  const points: Array<[number, number]> = [];
  for (let i = 0; i < raw.points.length; i++) {
    const pt = raw.points[i];
    if (
      !Array.isArray(pt) ||
      pt.length !== 2 ||
      !isFiniteNumber(pt[0]) ||
      !isFiniteNumber(pt[1])
    ) {
      return {
        ok: false,
        error: `whiteboard strokes[${index}].points[${i}] must be a [x, y] number pair`,
      };
    }
    points.push([pt[0], pt[1]]);
  }

  return {
    ok: true,
    value: {
      id: raw.id,
      tool: 'pen',
      color: colorR.value,
      width: raw.width,
      points,
    },
  };
}

function validateText(
  raw: unknown,
  index: number,
): ValidateResult<WhiteboardText> {
  if (!isPlainObject(raw)) {
    return { ok: false, error: `whiteboard texts[${index}] must be a plain object` };
  }
  const allowed = new Set(['id', 'x', 'y', 'text', 'color', 'fontSize']);
  for (const k of Object.keys(raw)) {
    if (!allowed.has(k)) {
      return {
        ok: false,
        error: `whiteboard texts[${index}] unknown field: ${k}`,
      };
    }
  }
  if (typeof raw.id !== 'string' || !UUID_SHAPE_RE.test(raw.id)) {
    return {
      ok: false,
      error: `whiteboard texts[${index}].id must be a UUID (8-4-4-4-12 hex, e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890)`,
    };
  }
  if (!isFiniteNumber(raw.x)) {
    return { ok: false, error: `whiteboard texts[${index}].x must be a number` };
  }
  if (!isFiniteNumber(raw.y)) {
    return { ok: false, error: `whiteboard texts[${index}].y must be a number` };
  }
  if (typeof raw.text !== 'string') {
    return { ok: false, error: `whiteboard texts[${index}].text must be a string` };
  }
  const text = raw.text.trim();
  if (!text) {
    return {
      ok: false,
      error: `whiteboard texts[${index}].text must be non-empty`,
    };
  }
  if (text.length > WHITEBOARD_TEXT_MAX) {
    return {
      ok: false,
      error: `whiteboard texts[${index}].text exceeds ${WHITEBOARD_TEXT_MAX} characters`,
    };
  }
  if (TEXT_LABEL_CONTROL.test(text)) {
    return {
      ok: false,
      error: `whiteboard texts[${index}].text contains control characters`,
    };
  }
  const colorR = validateColorString(raw.color, `whiteboard texts[${index}].color`);
  if (!colorR.ok) return colorR;

  if (!isFiniteNumber(raw.fontSize) || raw.fontSize <= 0) {
    return {
      ok: false,
      error: `whiteboard texts[${index}].fontSize must be a positive number`,
    };
  }
  if (raw.fontSize > WHITEBOARD_FONT_SIZE_MAX) {
    return {
      ok: false,
      error: `whiteboard texts[${index}].fontSize exceeds ${WHITEBOARD_FONT_SIZE_MAX}`,
    };
  }

  return {
    ok: true,
    value: {
      id: raw.id,
      x: raw.x,
      y: raw.y,
      text,
      color: colorR.value,
      fontSize: raw.fontSize,
    },
  };
}

export function validateWhiteboardState(
  data: unknown,
): ValidateResult<WhiteboardStateV1> {
  if (!isPlainObject(data)) {
    return { ok: false, error: 'whiteboard state must be a plain object' };
  }
  const o = data;
  const allowed = new Set(['format', 'width', 'height', 'background', 'strokes', 'texts']);
  for (const k of Object.keys(o)) {
    if (!allowed.has(k)) {
      return { ok: false, error: `whiteboard state unknown field: ${k}` };
    }
  }
  if (o.format !== WHITEBOARD_FORMAT) {
    return {
      ok: false,
      error: `whiteboard state format must be ${WHITEBOARD_FORMAT}, got ${String(o.format)}`,
    };
  }

  const widthR = validateDim(o.width, 'width');
  if (!widthR.ok) return widthR;
  const heightR = validateDim(o.height, 'height');
  if (!heightR.ok) return heightR;

  if (typeof o.background !== 'string') {
    return { ok: false, error: 'whiteboard state.background must be a string' };
  }
  const background = o.background.trim();
  if (!background) {
    return { ok: false, error: 'whiteboard state.background must be non-empty' };
  }
  if (background.length > WHITEBOARD_COLOR_MAX) {
    return {
      ok: false,
      error: `whiteboard state.background exceeds ${WHITEBOARD_COLOR_MAX} characters`,
    };
  }
  if (CONTROL_CHARS.test(background)) {
    return {
      ok: false,
      error: 'whiteboard state.background contains control characters',
    };
  }

  if (!Array.isArray(o.strokes)) {
    return { ok: false, error: 'whiteboard state.strokes must be an array' };
  }
  if (o.strokes.length > WHITEBOARD_STROKES_MAX) {
    return {
      ok: false,
      error: `whiteboard state.strokes exceeds max ${WHITEBOARD_STROKES_MAX}`,
    };
  }

  if (!Array.isArray(o.texts)) {
    return { ok: false, error: 'whiteboard state.texts must be an array' };
  }
  if (o.texts.length > WHITEBOARD_TEXTS_MAX) {
    return {
      ok: false,
      error: `whiteboard state.texts exceeds max ${WHITEBOARD_TEXTS_MAX}`,
    };
  }

  const strokeIds = new Set<string>();
  const strokes: WhiteboardStroke[] = [];
  let totalPoints = 0;
  for (let i = 0; i < o.strokes.length; i++) {
    const sr = validateStroke(o.strokes[i], i);
    if (!sr.ok) return sr;
    if (strokeIds.has(sr.value.id)) {
      return {
        ok: false,
        error: `whiteboard state.strokes duplicate id: ${sr.value.id}`,
      };
    }
    strokeIds.add(sr.value.id);
    totalPoints += sr.value.points.length;
    if (totalPoints > WHITEBOARD_POINTS_TOTAL_MAX) {
      return {
        ok: false,
        error: `whiteboard state total points exceeds max ${WHITEBOARD_POINTS_TOTAL_MAX}`,
      };
    }
    strokes.push(sr.value);
  }

  const textIds = new Set<string>();
  const texts: WhiteboardText[] = [];
  for (let i = 0; i < o.texts.length; i++) {
    const tr = validateText(o.texts[i], i);
    if (!tr.ok) return tr;
    if (textIds.has(tr.value.id)) {
      return {
        ok: false,
        error: `whiteboard state.texts duplicate id: ${tr.value.id}`,
      };
    }
    textIds.add(tr.value.id);
    texts.push(tr.value);
  }

  return {
    ok: true,
    value: {
      format: WHITEBOARD_FORMAT,
      width: widthR.value,
      height: heightR.value,
      background,
      strokes,
      texts,
    },
  };
}

export function validateWhiteboardProps(
  props: unknown,
): ValidateResult<WhiteboardProps> {
  if (!isPlainObject(props)) {
    return { ok: false, error: 'whiteboard props must be a plain object' };
  }
  const o = props;
  const allowed = new Set(['mode', 'bgColor', 'allowSubmit', 'submitLabel']);
  for (const k of Object.keys(o)) {
    if (!allowed.has(k)) {
      if (
        k === 'strokes' ||
        k === 'texts' ||
        k === 'png' ||
        k === 'image' ||
        k === 'data' ||
        k === 'format'
      ) {
        return {
          ok: false,
          error: `whiteboard props must not include '${k}' — put body in state`,
        };
      }
      return { ok: false, error: `whiteboard props unknown field: ${k}` };
    }
  }
  if (o.mode !== undefined) {
    if (o.mode !== 'edit' && o.mode !== 'view') {
      return {
        ok: false,
        error: `whiteboard props.mode must be 'edit' or 'view', got ${String(o.mode)}`,
      };
    }
  }
  if (o.bgColor !== undefined) {
    if (typeof o.bgColor !== 'string') {
      return { ok: false, error: 'whiteboard props.bgColor must be a string' };
    }
    if (o.bgColor.length > WHITEBOARD_COLOR_MAX) {
      return {
        ok: false,
        error: `whiteboard props.bgColor exceeds ${WHITEBOARD_COLOR_MAX} characters`,
      };
    }
    if (CONTROL_CHARS.test(o.bgColor)) {
      return {
        ok: false,
        error: 'whiteboard props.bgColor contains control characters',
      };
    }
  }
  if (o.allowSubmit !== undefined && typeof o.allowSubmit !== 'boolean') {
    return { ok: false, error: 'whiteboard props.allowSubmit must be a boolean' };
  }
  if (o.submitLabel !== undefined) {
    if (typeof o.submitLabel !== 'string') {
      return { ok: false, error: 'whiteboard props.submitLabel must be a string' };
    }
    const label = o.submitLabel.trim();
    if (!label) {
      return {
        ok: false,
        error: 'whiteboard props.submitLabel must be non-empty when present',
      };
    }
    if (label.length > WHITEBOARD_SUBMIT_LABEL_MAX) {
      return {
        ok: false,
        error: `whiteboard props.submitLabel exceeds ${WHITEBOARD_SUBMIT_LABEL_MAX} characters`,
      };
    }
    if (CONTROL_CHARS.test(label)) {
      return {
        ok: false,
        error: 'whiteboard props.submitLabel contains control characters',
      };
    }
  }
  return {
    ok: true,
    value: {
      ...(o.mode !== undefined ? { mode: o.mode as 'edit' | 'view' } : {}),
      ...(typeof o.bgColor === 'string' ? { bgColor: o.bgColor } : {}),
      ...(typeof o.allowSubmit === 'boolean' ? { allowSubmit: o.allowSubmit } : {}),
      ...(typeof o.submitLabel === 'string'
        ? { submitLabel: o.submitLabel.trim() }
        : {}),
    },
  };
}
